/**
 * Cuánto cuesta la IA de verdad (admin).
 *
 * Cada llamada a la IA deja una fila en `ai_usage_events` con sus tokens y su
 * costo en dólares. Aquí se resume para decidir los topes de cada plan con
 * datos y no con estimaciones:
 *   · por función: lo que cuesta un sitio, una página, un mensaje del editor,
 *     una pregunta del asistente (media y p90);
 *   · por plan: lo que gasta una cuenta en el periodo, contra lo que paga;
 *   · por día, y las cuentas que más gastan.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconCoin } from '@tabler/icons-react'
import { getAiSpend, type AdminAiSpend as Spend } from '../../lib/api'
import { activeLanguage, translateServerError, useI18n } from '../../lib/i18n'

const FEATURE_KEYS: Record<string, string> = {
  site: 'aiSpend.feature.site',
  page: 'aiSpend.feature.page',
  editor_chat: 'aiSpend.feature.editorChat',
  editor_plan: 'aiSpend.feature.editorPlan',
  assistant: 'aiSpend.feature.assistant',
  write_post: 'aiSpend.feature.writePost',
  draft_offer: 'aiSpend.feature.draftOffer',
  photo_notes: 'aiSpend.feature.photoNotes',
}

export function AdminAiSpend({ token, days }: { token: string; days: number }) {
  const { t } = useI18n()
  const [data, setData] = useState<Spend | null>(null)
  const [failed, setFailed] = useState('')

  const load = useCallback(async () => {
    setFailed('')
    setData(null)
    try {
      setData(await getAiSpend(token, days))
    } catch (err) {
      setFailed(translateServerError(err, t('aiUnmet.loadFailed')))
    }
  }, [token, days, t])

  useEffect(() => { void load() }, [load])

  const language = activeLanguage()
  /* Dólares con las decimales que hagan falta: un mensaje cuesta milésimas. */
  const usd = (value: number) => {
    const digits = value === 0 ? 2 : value < 0.01 ? 5 : value < 1 ? 4 : 2
    return new Intl.NumberFormat(language, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)
  }
  const n = (value: number) => new Intl.NumberFormat(language).format(value)

  if (failed) {
    return (
      <div className="dash-inline-msg">
        <span>{failed}</span>
        <div className="dash-inline-msg-actions">
          <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('aiUnmet.retry')}</button>
        </div>
      </div>
    )
  }
  if (!data) return <p className="dash-card-description">{t('aiUnmet.loading')}</p>
  if (!data.byFeature.length) {
    return (
      <div className="dash-placeholder">
        <IconCoin size={34} aria-hidden="true" />
        <strong>{t('aiSpend.empty')}</strong>
        <span>{t('aiSpend.emptyBody')}</span>
      </div>
    )
  }

  return (
    <div className="dash-ai-spend">
      <p className="dash-ai-spend-total">
        <strong>{usd(data.total)}</strong> {t('aiSpend.totalIn', { days })}
      </p>

      <h3 className="dash-ai-spend-title">{t('aiSpend.byFeature')}</h3>
      <div className="dash-ai-unmet-wrap">
        <table className="dash-ai-unmet-table">
          <thead>
            <tr>
              <th>{t('aiSpend.col.feature')}</th>
              <th className="is-number">{t('aiSpend.col.calls')}</th>
              <th className="is-number">{t('aiSpend.col.avgCost')}</th>
              <th className="is-number">{t('aiSpend.col.p90Cost')}</th>
              <th className="is-number">{t('aiSpend.col.tokens')}</th>
              <th className="is-number">{t('aiSpend.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.byFeature.map((row) => (
              <tr key={row.feature}>
                <td>
                  <strong>{FEATURE_KEYS[row.feature] ? t(FEATURE_KEYS[row.feature]!) : row.feature}</strong>
                  <small>
                    {t('aiSpend.people', { count: row.users })}
                    {row.cached ? ' · ' + t('aiSpend.fromCache', { count: row.cached }) : ''}
                    {row.estimated ? ' · ' + t('aiSpend.estimated', { count: row.estimated }) : ''}
                  </small>
                </td>
                <td className="is-number">{n(row.calls)}</td>
                <td className="is-number">{usd(row.avgCost)}</td>
                <td className="is-number">{usd(row.p90Cost)}</td>
                <td className="is-number">
                  {n(row.avgInput)} / {n(row.avgOutput)}
                  <small>{t('aiSpend.p90Tokens', { count: n(row.p90Tokens) })}</small>
                </td>
                <td className="is-number">{usd(row.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="dash-ai-spend-title">{t('aiSpend.byPlan')}</h3>
      <div className="dash-ai-unmet-wrap">
        <table className="dash-ai-unmet-table">
          <thead>
            <tr>
              <th>{t('aiSpend.col.plan')}</th>
              <th className="is-number">{t('aiSpend.col.accounts')}</th>
              <th className="is-number">{t('aiSpend.col.perAccount')}</th>
              <th className="is-number">{t('aiSpend.col.p90Account')}</th>
              <th className="is-number">{t('aiSpend.col.maxAccount')}</th>
              <th className="is-number">{t('aiSpend.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.byPlan.map((row) => (
              <tr key={row.plan}>
                <td><strong>{row.plan}</strong><small>{t('aiSpend.callsPerAccount', { count: row.avgCalls })}</small></td>
                <td className="is-number">{n(row.users)}</td>
                <td className="is-number">{usd(row.avgCost)}</td>
                <td className="is-number">{usd(row.p90Cost)}</td>
                <td className="is-number">{usd(row.maxCost)}</td>
                <td className="is-number">{usd(row.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="dash-ai-spend-title">{t('aiSpend.topUsers')}</h3>
      <div className="dash-ai-unmet-wrap">
        <table className="dash-ai-unmet-table">
          <thead>
            <tr>
              <th>{t('aiSpend.col.account')}</th>
              <th>{t('aiSpend.col.plan')}</th>
              <th className="is-number">{t('aiSpend.col.calls')}</th>
              <th className="is-number">{t('aiSpend.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.topUsers.map((row) => (
              <tr key={row.userId}>
                <td><strong>{row.name || row.email || row.userId.slice(0, 8)}</strong>{row.email && row.name ? <small>{row.email}</small> : null}</td>
                <td>{row.plan}</td>
                <td className="is-number">{n(row.calls)}</td>
                <td className="is-number">{usd(row.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="dash-ai-spend-title">{t('aiSpend.byDay')}</h3>
      <div className="dash-ai-unmet-wrap">
        <table className="dash-ai-unmet-table">
          <thead>
            <tr>
              <th>{t('aiSpend.col.day')}</th>
              <th className="is-number">{t('aiSpend.col.calls')}</th>
              <th className="is-number">{t('aiSpend.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {[...data.byDay].reverse().map((row) => (
              <tr key={row.day}>
                <td>{new Date(row.day + 'T12:00:00').toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                <td className="is-number">{n(row.calls)}</td>
                <td className="is-number">{usd(row.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
